/**
 * "Stickers afdrukken" — een vel etiketten om op de voerbak of de zak te plakken.
 *
 * <p>De wens (§1 Dierenhotel Marijke) is dat de sticker blijft: aan de voerkeuken staat niemand met
 * een scherm, en wat een dier krijgt moet op de bak staan. Eén sticker per dier van vandaag, met de
 * naam groot, het hok erbij, en daaronder wat er in moet.
 *
 * <p>Client-side, net als {@code printHokstickers}: wat je op het scherm ziet is wat er uit de
 * printer komt, en er hoeft geen sjabloon op de server bij te blijven.
 */

/** Eén dier op het vel. */
export interface Voersticker {
  dierNaam: string;
  kennel: string | null;
  voer: string | null;
  hoeveelheid: string | null;
  voertijden: string | null;
  dieet: string | null;
  notitie: string | null;
}

export interface VoerstickersPrintOptions {
  /** Kop boven het vel; meestal de naam van de opvang. */
  organisatie: string;
  stickers: Voersticker[];
  /** Datum van afdrukken, al opgemaakt — zodat een oud vel herkenbaar is. */
  datum: string;
  /** De vaste teksten, zodat dit bestand geen vertaling hoeft te kennen. */
  labels: {
    titel: string;
    voer: string;
    hoeveelheid: string;
    tijden: string;
    dieet: string;
    notitie: string;
  };
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function regel(label: string, waarde: string | null): string {
  if (!waarde || !waarde.trim()) return "";
  return `<div class="r"><span class="l">${esc(label)}</span><span class="w">${esc(waarde)}</span></div>`;
}

function stickerHtml(s: Voersticker, labels: VoerstickersPrintOptions["labels"]): string {
  return `
    <div class="sticker">
      <div class="kop">
        <span class="naam">${esc(s.dierNaam)}</span>
        ${s.kennel ? `<span class="hok">${esc(s.kennel)}</span>` : ""}
      </div>
      ${regel(labels.voer, s.voer)}
      ${regel(labels.hoeveelheid, s.hoeveelheid)}
      ${regel(labels.tijden, s.voertijden)}
      ${regel(labels.dieet, s.dieet)}
      ${s.notitie ? `<div class="notitie">${esc(s.notitie)}</div>` : ""}
    </div>`;
}

/** Bouwt het vel en zet het in een printvenster. */
export function printVoerstickers(opties: VoerstickersPrintOptions): void {
  const html = `<!doctype html>
<html lang="nl">
<head>
<meta charset="utf-8" />
<title>${esc(opties.labels.titel)}</title>
<style>
  @page { size: A4; margin: 10mm; }
  body { font-family: system-ui, "Segoe UI", Arial, sans-serif; color: #25313b; margin: 0; }
  .vel { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6mm; }
  .kopregel { display: flex; justify-content: space-between; align-items: baseline;
              margin: 0 0 6mm; font-size: 11pt; }
  .kopregel strong { font-size: 13pt; }
  .sticker { border: 1px solid #25313b; border-radius: 3mm; padding: 5mm; min-height: 40mm;
             break-inside: avoid; }
  .kop { display: flex; align-items: baseline; gap: 4mm; border-bottom: 1px solid #c8d2d9;
         padding-bottom: 2mm; margin-bottom: 3mm; }
  .naam { font-size: 18pt; font-weight: 800; letter-spacing: -0.01em; }
  .hok { margin-left: auto; font-size: 13pt; font-weight: 700; }
  .r { display: flex; gap: 3mm; font-size: 11pt; line-height: 1.5; }
  .l { min-width: 26mm; color: #5a6b78; }
  .w { font-weight: 600; }
  .notitie { margin-top: 2mm; padding-top: 2mm; border-top: 1px dashed #c8d2d9; font-size: 10.5pt; }
</style>
</head>
<body>
  <div class="kopregel">
    <strong>${esc(opties.organisatie)}</strong>
    <span>${esc(opties.labels.titel)} — ${esc(opties.datum)}</span>
  </div>
  <div class="vel">${opties.stickers.map((s) => stickerHtml(s, opties.labels)).join("")}</div>
</body>
</html>`;

  const venster = window.open("", "_blank");
  if (!venster) return;
  venster.document.write(html);
  venster.document.close();
  venster.focus();
  venster.print();
}
